import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Save } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { CompetitionSportField } from "../sports/CompetitionSportField";

export const EMPTY_COMPETITION_FORM = {
	name: "",
	date: "",
	end_date: "",
	event_type: "",
	discipline: "",
	sport: null,
	priority: "",
	category: "",
	status: "",
	location: "",
	notes: "",
};

export const competitionFormFromEvent = (event) => ({
	name: event?.name || "",
	date: event?.date || "",
	end_date: event?.end_date || "",
	event_type: event?.event_type || "",
	discipline: event?.discipline || "",
	// Lo sport del catalogo, anche quello che il server ha ricavato dal testo:
	// salvando dal form il collegamento diventa vero (D16 di docs/sport-config).
	sport: event?.sport || null,
	priority: event?.priority || "",
	category: event?.category || "",
	status: event?.status || "",
	location: event?.location || "",
	notes: event?.notes || "",
});

/**
 * Shared create/edit form for a competition event. The form state lives in the
 * caller so each surface keeps its own save semantics (single athlete, group
 * fan-out, ...); this component only renders the fields.
 */
export function CompetitionEventDialog({
	open,
	onOpenChange,
	form,
	setForm,
	onSave,
	isEditing = false,
}) {
	const { t } = useLanguage();
	const setField = (field) => (value) =>
		setForm((current) => ({ ...current, [field]: value }));

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="bg-card border-border rounded-sm max-w-md">
				<DialogDescription className="hidden" />
				<DialogHeader>
					<DialogTitle className="font-heading">
						{isEditing ? t("editEvent") : t("newEvent")}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<div>
						<label className="label-tactical mb-1 block">{t("name")}</label>
						<Input
							className="bg-background border-border rounded-sm"
							value={form.name}
							onChange={(e) => setField("name")(e.target.value)}
							placeholder={t("eventNamePlaceholder")}
						/>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<label className="label-tactical mb-1 block">{t("date")}</label>
							<Input
								type="date"
								className="bg-background border-border rounded-sm"
								value={form.date}
								onChange={(e) => setField("date")(e.target.value)}
							/>
						</div>
						<div>
							<label className="label-tactical mb-1 block">{t("type")}</label>
							<Input
								className="bg-background border-border rounded-sm"
								value={form.event_type}
								onChange={(e) => setField("event_type")(e.target.value)}
								placeholder={t("competitionTypePlaceholder")}
							/>
						</div>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<label className="label-tactical mb-1 block">
								{t("competitionPriority")}
							</label>
							<Select
								value={form.priority || "__none__"}
								onValueChange={(value) =>
									setField("priority")(value === "__none__" ? "" : value)
								}
							>
								<SelectTrigger className="bg-background border-border rounded-sm">
									<SelectValue placeholder="—" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="__none__">—</SelectItem>
									<SelectItem value="A">{t("competitionPriorityA")}</SelectItem>
									<SelectItem value="B">{t("competitionPriorityB")}</SelectItem>
									<SelectItem value="C">{t("competitionPriorityC")}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div>
							<label className="label-tactical mb-1 block">
								{t("competitionStatus")}
							</label>
							<Select
								value={form.status || "__none__"}
								onValueChange={(value) =>
									setField("status")(value === "__none__" ? "" : value)
								}
							>
								<SelectTrigger className="bg-background border-border rounded-sm">
									<SelectValue placeholder="—" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="__none__">—</SelectItem>
									<SelectItem value="planned">
										{t("competitionStatusPlanned")}
									</SelectItem>
									<SelectItem value="confirmed">
										{t("competitionStatusConfirmed")}
									</SelectItem>
									<SelectItem value="registered">
										{t("competitionStatusRegistered")}
									</SelectItem>
									<SelectItem value="done">
										{t("competitionStatusDone")}
									</SelectItem>
									<SelectItem value="cancelled">
										{t("competitionStatusCancelled")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<label className="label-tactical mb-1 block">
								{t("competitionSport")}
							</label>
							<CompetitionSportField form={form} setForm={setForm} />
						</div>
						<div>
							<label className="label-tactical mb-1 block">
								{t("competitionCategory")}
							</label>
							<Input
								className="bg-background border-border rounded-sm"
								value={form.category}
								onChange={(e) => setField("category")(e.target.value)}
								placeholder={t("competitionCategoryPlaceholder")}
							/>
						</div>
					</div>
					<div>
						<label className="label-tactical mb-1 block">
							{t("competitionEndDate")}
						</label>
						<Input
							type="date"
							className="bg-background border-border rounded-sm"
							value={form.end_date}
							onChange={(e) => setField("end_date")(e.target.value)}
						/>
					</div>
					<div>
						<label className="label-tactical mb-1 block">
							{t("locationLabel")}
						</label>
						<Input
							className="bg-background border-border rounded-sm"
							value={form.location}
							onChange={(e) => setField("location")(e.target.value)}
						/>
					</div>
					<div>
						<label className="label-tactical mb-1 block">{t("notes")}</label>
						<Textarea
							className="bg-background border-border rounded-sm min-h-[80px]"
							value={form.notes}
							onChange={(e) => setField("notes")(e.target.value)}
							placeholder={t("eventNotesPlaceholder")}
						/>
					</div>
					<div className="flex justify-end gap-2 pt-2">
						<Button
							variant="ghost"
							className="rounded-sm"
							onClick={() => onOpenChange(false)}
						>
							{t("cancel")}
						</Button>
						<Button
							className="bg-volt hover:bg-volt/90 rounded-sm"
							onClick={onSave}
						>
							<Save className="w-4 h-4 mr-1" />
							{isEditing ? t("save") : t("addEvent")}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
